import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../App';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { FileEdit, Plus, Trash2, Edit3, Save, Bug, Lightbulb, Mail, Inbox, Copy, GripVertical } from 'lucide-react';
import { toast } from 'sonner';

const FIELD_TYPES = [
  { id: 'text', label: 'Short Text' },
  { id: 'textarea', label: 'Long Text' },
  { id: 'email', label: 'Email' },
  { id: 'number', label: 'Number' },
  { id: 'url', label: 'URL' },
  { id: 'select', label: 'Dropdown' },
  { id: 'radio', label: 'Multiple Choice (one)' },
  { id: 'checkbox_group', label: 'Multi-select Checkboxes' },
  { id: 'checkbox', label: 'Single Checkbox' },
  { id: 'rating', label: 'Star Rating (1-5)' },
  { id: 'date', label: 'Date' },
  { id: 'file', label: 'File Upload (URL)' },
];

const OPERATORS = [
  { id: 'equals', label: 'is' },
  { id: 'not_equals', label: 'is not' },
  { id: 'contains', label: 'contains' },
  { id: 'not_empty', label: 'is filled' },
  { id: 'empty', label: 'is empty' },
];

const PRESET_ICONS = { bug: Bug, feature: Lightbulb, contact: Mail };

const Forms = ({ user, setUser }) => {
  const [forms, setForms] = useState([]);
  const [presets, setPresets] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [showPresetPicker, setShowPresetPicker] = useState(false);
  const [submissionsModal, setSubmissionsModal] = useState(null);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [fRes, pRes] = await Promise.all([
        axios.get(`${API_URL}/forms`),
        axios.get(`${API_URL}/forms/presets`),
      ]);
      setForms(fRes.data.forms || []);
      setPresets(pRes.data.presets || {});
    } catch (e) {
      toast.error('Failed to load forms');
    } finally {
      setLoading(false);
    }
  };

  const openCreateBlank = () => {
    setEditing({
      name: 'New Form', slug: 'new-form', title: 'Tell us more',
      description: '', fields: [], submit_button_text: 'Submit',
      success_message: 'Thanks! Your submission has been received.',
      is_active: true, notification_emails: [], preset: 'custom',
    });
    setShowDialog(true);
    setShowPresetPicker(false);
  };

  const openFromPreset = (presetId) => {
    const p = presets[presetId];
    if (!p) return;
    setEditing({
      name: p.name, slug: p.slug, title: p.title, description: p.description,
      fields: (p.fields || []).map(f => ({ ...f, id: f.id || `f-${Date.now()}-${Math.random().toString(36).slice(2, 8)}` })),
      submit_button_text: 'Submit',
      success_message: 'Thanks! Your submission has been received.',
      is_active: true, notification_emails: [], preset: presetId,
    });
    setShowDialog(true);
    setShowPresetPicker(false);
  };

  const openEdit = async (form) => {
    setEditing({ ...form });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!editing.name || !editing.title) {
      toast.error('Name and title are required');
      return;
    }
    try {
      const payload = {
        name: editing.name, slug: editing.slug, title: editing.title,
        description: editing.description, fields: editing.fields,
        submit_button_text: editing.submit_button_text,
        success_message: editing.success_message,
        is_active: editing.is_active,
        notification_emails: editing.notification_emails,
        webhook_url: editing.webhook_url || null,
        preset: editing.preset || 'custom',
      };
      if (editing.id) {
        await axios.patch(`${API_URL}/forms/${editing.id}`, payload);
        toast.success('Form updated');
      } else {
        await axios.post(`${API_URL}/forms`, payload);
        toast.success('Form created');
      }
      setShowDialog(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this form?')) return;
    try {
      await axios.delete(`${API_URL}/forms/${id}`);
      toast.success('Deleted');
      fetchAll();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const addField = () => {
    const newField = {
      id: `f-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      label: 'New Field', type: 'text', placeholder: '', required: false, options: [], help_text: '',
    };
    setEditing({ ...editing, fields: [...(editing.fields || []), newField] });
  };

  const updateField = (idx, updates) => {
    const fields = [...editing.fields];
    fields[idx] = { ...fields[idx], ...updates };
    setEditing({ ...editing, fields });
  };

  const removeField = (idx) => {
    setEditing({ ...editing, fields: editing.fields.filter((_, i) => i !== idx) });
  };

  const moveField = (from, to) => {
    if (to < 0 || to >= editing.fields.length) return;
    const fields = [...editing.fields];
    const [item] = fields.splice(from, 1);
    fields.splice(to, 0, item);
    setEditing({ ...editing, fields });
  };

  const viewSubmissions = async (form) => {
    try {
      const res = await axios.get(`${API_URL}/forms/${form.id}/submissions`);
      setSubmissionsModal({ form, submissions: res.data.submissions || [] });
    } catch (e) {
      toast.error('Failed to load submissions');
    }
  };

  return (
    <Layout user={user} setUser={setUser}>
      <div className="p-8 max-w-7xl mx-auto" data-testid="forms-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-violet-600">
                <FileEdit className="h-5 w-5 text-white" />
              </div>
              <h1 className="text-4xl font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }}>Forms</h1>
            </div>
            <p className="text-sm text-zinc-600">Bug reports, feature requests, custom contact forms — collect input from your users.</p>
          </div>
          <Button onClick={() => setShowPresetPicker(true)} className="bg-violet-600 hover:bg-violet-700" data-testid="new-form-button">
            <Plus className="h-4 w-4 mr-2" />New Form
          </Button>
        </div>

        {loading ? <p className="text-zinc-500">Loading…</p> : forms.length === 0 ? (
          <div className="text-center py-16 border-2 border-dashed border-zinc-200 rounded-2xl bg-zinc-50/50">
            <FileEdit className="h-12 w-12 mx-auto text-zinc-300 mb-3" />
            <p className="text-zinc-500 mb-4">No forms yet</p>
            <Button onClick={() => setShowPresetPicker(true)}><Plus className="h-4 w-4 mr-2" />Create your first form</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {forms.map(f => {
              const Icon = PRESET_ICONS[f.preset] || FileEdit;
              return (
                <div key={f.id} className="bg-white border border-zinc-200 rounded-2xl p-5 hover:shadow-sm transition-shadow" data-testid={`form-card-${f.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="h-10 w-10 rounded-xl bg-violet-50 flex items-center justify-center">
                      <Icon className="h-5 w-5 text-violet-600" />
                    </div>
                    {!f.is_active && <span className="text-xs px-2 py-0.5 bg-zinc-100 text-zinc-500 rounded">Inactive</span>}
                  </div>
                  <h3 className="font-bold text-zinc-900">{f.title}</h3>
                  <p className="text-xs text-zinc-500 mt-1">/{f.slug} · {(f.fields || []).length} field{f.fields?.length === 1 ? '' : 's'}</p>
                  <div className="flex gap-2 mt-4">
                    <Button size="sm" variant="outline" onClick={() => viewSubmissions(f)} className="flex-1"><Inbox className="h-3 w-3 mr-1" />Submissions</Button>
                    <Button size="sm" variant="outline" onClick={() => openEdit(f)} className="px-2"><Edit3 className="h-3 w-3" /></Button>
                    <Button size="sm" variant="outline" onClick={() => handleDelete(f.id)} className="px-2 text-red-500"><Trash2 className="h-3 w-3" /></Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Preset Picker */}
        <Dialog open={showPresetPicker} onOpenChange={setShowPresetPicker}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Start from a template</DialogTitle>
              <DialogDescription>Pick a preset to get started, or build from scratch.</DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              {Object.entries(presets).map(([id, p]) => {
                const Icon = PRESET_ICONS[id] || FileEdit;
                return (
                  <button
                    key={id}
                    onClick={() => openFromPreset(id)}
                    className="w-full flex items-start gap-3 p-4 rounded-xl border border-zinc-200 hover:border-violet-300 hover:bg-violet-50/30 transition-all text-left"
                    data-testid={`preset-${id}`}
                  >
                    <div className="h-10 w-10 rounded-lg bg-violet-50 flex items-center justify-center flex-shrink-0">
                      <Icon className="h-5 w-5 text-violet-600" />
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-zinc-900">{p.name}</div>
                      <div className="text-xs text-zinc-500 mt-0.5">{p.description}</div>
                      <div className="text-xs text-zinc-400 mt-1">{(p.fields || []).length} preset fields</div>
                    </div>
                  </button>
                );
              })}
              <button
                onClick={openCreateBlank}
                className="w-full flex items-start gap-3 p-4 rounded-xl border-2 border-dashed border-zinc-200 hover:border-violet-300 transition-all text-left"
                data-testid="preset-blank"
              >
                <div className="h-10 w-10 rounded-lg bg-zinc-50 flex items-center justify-center">
                  <Plus className="h-5 w-5 text-zinc-600" />
                </div>
                <div>
                  <div className="font-semibold text-zinc-900">Blank Form</div>
                  <div className="text-xs text-zinc-500 mt-0.5">Build from scratch with the form builder</div>
                </div>
              </button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Form Editor */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing?.id ? 'Edit Form' : 'New Form'}</DialogTitle>
              <DialogDescription>Configure form details and add fields with the builder below.</DialogDescription>
            </DialogHeader>
            {editing && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm font-medium">Internal Name</Label>
                    <Input value={editing.name} onChange={e => setEditing({...editing, name: e.target.value})} className="mt-1.5" data-testid="form-name-input" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">URL Slug</Label>
                    <Input value={editing.slug} onChange={e => setEditing({...editing, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-')})} className="mt-1.5" />
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Public Title</Label>
                  <Input value={editing.title} onChange={e => setEditing({...editing, title: e.target.value})} className="mt-1.5" data-testid="form-title-input" />
                </div>
                <div>
                  <Label className="text-sm font-medium">Description</Label>
                  <Textarea value={editing.description || ''} onChange={e => setEditing({...editing, description: e.target.value})} rows={2} className="mt-1.5" />
                </div>

                {/* Fields builder */}
                <div className="border border-zinc-200 rounded-xl p-4 bg-zinc-50/30">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-sm font-bold">Form Fields</Label>
                    <Button size="sm" onClick={addField} variant="outline" data-testid="add-field-button"><Plus className="h-3 w-3 mr-1" />Add Field</Button>
                  </div>
                  <div className="space-y-2">
                    {(editing.fields || []).map((field, idx) => (
                      <div key={field.id || idx} className="bg-white border border-zinc-200 rounded-lg p-3" data-testid={`field-row-${idx}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <div className="flex flex-col">
                            <button onClick={() => moveField(idx, idx - 1)} className="p-0.5 hover:bg-zinc-100 rounded text-zinc-400"><GripVertical className="h-3 w-3 rotate-90" /></button>
                          </div>
                          <Input value={field.label} onChange={e => updateField(idx, { label: e.target.value })} placeholder="Field label" className="flex-1 h-9" />
                          <select value={field.type} onChange={e => updateField(idx, { type: e.target.value })} className="h-9 px-2 rounded border border-zinc-200 text-sm bg-white">
                            {FIELD_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                          </select>
                          <label className="flex items-center gap-1 text-xs text-zinc-600">
                            <input type="checkbox" checked={field.required} onChange={e => updateField(idx, { required: e.target.checked })} className="rounded" />
                            Required
                          </label>
                          <button onClick={() => removeField(idx)} className="p-1 hover:bg-red-50 rounded text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                        <div className="ml-5 flex gap-2 items-center">
                          <Input value={field.placeholder || ''} onChange={e => updateField(idx, { placeholder: e.target.value })} placeholder="Placeholder text (optional)" className="h-8 text-xs flex-1" />
                          {(field.type === 'select' || field.type === 'radio' || field.type === 'checkbox_group') && (
                            <Input
                              value={(field.options || []).join(', ')}
                              onChange={e => updateField(idx, { options: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                              placeholder="Option 1, Option 2, …"
                              className="h-8 text-xs flex-1"
                            />
                          )}
                        </div>
                        {/* Conditional visibility */}
                        <div className="ml-5 mt-2 pt-2 border-t border-zinc-100">
                          <div className="flex items-center gap-2 text-xs text-zinc-600">
                            <span className="font-semibold">Show only if</span>
                            <select
                              value={field.show_if?.field_id || ''}
                              onChange={e => updateField(idx, {
                                show_if: e.target.value ? { field_id: e.target.value, operator: field.show_if?.operator || 'equals', value: field.show_if?.value || '' } : null,
                              })}
                              className="h-7 px-1.5 rounded border border-zinc-200 bg-white text-xs"
                              data-testid={`field-cond-source-${idx}`}
                            >
                              <option value="">— always show —</option>
                              {(editing.fields || []).filter((_, j) => j !== idx).map(f => (
                                <option key={f.id} value={f.id}>{f.label || 'Untitled'}</option>
                              ))}
                            </select>
                            {field.show_if?.field_id && (
                              <>
                                <select
                                  value={field.show_if?.operator || 'equals'}
                                  onChange={e => updateField(idx, { show_if: { ...field.show_if, operator: e.target.value } })}
                                  className="h-7 px-1.5 rounded border border-zinc-200 bg-white text-xs"
                                  data-testid={`field-cond-op-${idx}`}
                                >
                                  {OPERATORS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                                </select>
                                {!['empty', 'not_empty'].includes(field.show_if?.operator) && (
                                  <Input
                                    value={field.show_if?.value || ''}
                                    onChange={e => updateField(idx, { show_if: { ...field.show_if, value: e.target.value } })}
                                    placeholder="value"
                                    className="h-7 text-xs flex-1 max-w-[160px]"
                                    data-testid={`field-cond-value-${idx}`}
                                  />
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    {(editing.fields || []).length === 0 && (
                      <div className="text-center py-6 text-sm text-zinc-400 border-2 border-dashed border-zinc-200 rounded-lg">
                        No fields yet — click "Add Field" to begin
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm font-medium">Submit Button Text</Label>
                    <Input value={editing.submit_button_text} onChange={e => setEditing({...editing, submit_button_text: e.target.value})} className="mt-1.5" />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg border border-zinc-200 mt-6">
                    <span className="text-sm">Active</span>
                    <Switch checked={!!editing.is_active} onCheckedChange={v => setEditing({...editing, is_active: v})} />
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Success Message</Label>
                  <Input value={editing.success_message} onChange={e => setEditing({...editing, success_message: e.target.value})} className="mt-1.5" />
                </div>
                <div>
                  <Label className="text-sm font-medium">Notification Emails (comma-separated)</Label>
                  <Input
                    value={(editing.notification_emails || []).join(', ')}
                    onChange={e => setEditing({...editing, notification_emails: e.target.value.split(',').map(s => s.trim()).filter(Boolean)})}
                    placeholder="alerts@example.com, support@example.com"
                    className="mt-1.5"
                  />
                  <p className="text-xs text-zinc-500 mt-1">Falls back to workspace contact recipients if empty.</p>
                </div>

                <div>
                  <Label className="text-sm font-medium">Webhook URL (optional)</Label>
                  <div className="flex gap-2 mt-1.5">
                    <Input
                      value={editing.webhook_url || ''}
                      onChange={e => setEditing({...editing, webhook_url: e.target.value})}
                      placeholder="https://hooks.zapier.com/… or your own endpoint"
                      data-testid="webhook-url-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={async () => {
                        if (!editing.webhook_url) { toast.error('Enter a webhook URL'); return; }
                        try {
                          const r = await axios.post(`${API_URL}/forms/webhook/test`, { url: editing.webhook_url });
                          if (r.data.ok) toast.success(`Webhook OK (${r.data.status_code})`);
                          else toast.error(`Failed: ${r.data.error || r.data.status_code}`);
                        } catch (e) { toast.error('Test failed'); }
                      }}
                      data-testid="webhook-test-button"
                    >
                      Test
                    </Button>
                  </div>
                  <p className="text-xs text-zinc-500 mt-1">
                    POSTs JSON <code className="text-xs bg-zinc-100 px-1 py-0.5 rounded">{'{event:"form.submitted", workspace, form, submission}'}</code> on every submission. Retries once on failure.
                  </p>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
                  <Button onClick={handleSave} className="bg-violet-600 hover:bg-violet-700" data-testid="form-save-button">
                    <Save className="h-4 w-4 mr-2" />Save Form
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Submissions Viewer */}
        <Dialog open={!!submissionsModal} onOpenChange={(o) => !o && setSubmissionsModal(null)}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Submissions — {submissionsModal?.form?.title}</DialogTitle>
              <DialogDescription>{submissionsModal?.submissions?.length || 0} submission{submissionsModal?.submissions?.length === 1 ? '' : 's'}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              {(submissionsModal?.submissions || []).length === 0 ? (
                <p className="text-center py-8 text-zinc-500">No submissions yet</p>
              ) : (
                submissionsModal.submissions.map(s => (
                  <div key={s.id} className="bg-zinc-50 border border-zinc-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-xs text-zinc-500">{new Date(s.created_at).toLocaleString()}</div>
                      <div className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded">{s.status || 'new'}</div>
                    </div>
                    <div className="space-y-1">
                      {Object.entries(s.data || {}).map(([k, v]) => (
                        <div key={k} className="grid grid-cols-3 gap-2 text-sm">
                          <span className="text-zinc-500 font-medium">{k}:</span>
                          <span className="col-span-2 text-zinc-900">{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Forms;
